import { type ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import Alert from '@/components/atoms/Alert';
import { type ProviderFieldSchema, scadaItemCount } from '@/domain/providerConfig';

import { ClientAdminViewContext } from './fieldContexts';
import { conflictText, type ProviderFieldConflict } from './fieldText';

import styles from './ProviderSchemaField.module.scss';

/** A value stored at the field's key that the field cannot show, kept so it is not hidden. */
export interface UnreadableStored {
    type: string;
    /** JSON text, or null where the server withholds it (a client admin gets the type only). */
    value: string | null;
    /** For a SCADA list, the entry (from 1) and the copy key of its part; else null. */
    entry: { number: number; partKey: string | null } | null;
    /** A save cannot write the field, so it is not required either. */
    blocked: boolean;
}

/** The JSON type names the server reports (stored-leftovers.ts jsonTypeName). */
export const JSON_TYPE_NAMES = ['null', 'list', 'object', 'string', 'number', 'boolean'] as const;

/**
 * The notes a viewer gets. A client admin is not shown what is stored, so a write would
 * replace data they never saw; the field is read-only for them (blocked), the server refuses
 * the write too (unreadableStored), and one note says whom to ask.
 */
export function viewerNotes(
    unreadable: readonly UnreadableStored[],
    clientAdmin: boolean
): readonly UnreadableStored[] {
    if (!clientAdmin || unreadable.length === 0) return unreadable;
    return [{ ...unreadable[0], blocked: true, entry: null }];
}

/** The ids of the stored-value notes under the field with id `id`, which describe its box. */
export function unreadableIds(id: string, unreadable: readonly UnreadableStored[]): string[] {
    return unreadable.map((_, index) => `${id}-unreadable-${index}`);
}

/**
 * A setting's hint for FormField: its help, then the default an empty value uses, each on a
 * line of its own. Null when it has neither.
 */
export function fieldHint(
    field: ProviderFieldSchema,
    defaultText: string | null,
    t: TFunction
): ReactNode {
    if (!field.helpKey && defaultText === null) return null;
    return (
        <>
            {field.helpKey && <span className={styles.hintLine}>{t(field.helpKey)}</span>}
            {defaultText !== null && (
                <span className={styles.hintLine}>
                    {t('providerConfig.defaults.help', { value: defaultText })}
                </span>
            )}
        </>
    );
}

/**
 * What the note says of one stored value the field cannot show. A client admin cannot act on
 * a blocked one, so is told whom to ask rather than what is stored.
 */
function unreadableText(t: TFunction, entry: UnreadableStored, clientAdmin: boolean): string {
    if (entry.blocked && clientAdmin) return t('providerConfig.unreadableBlockedClient');
    const type = t(`providerConfig.jsonTypes.${entry.type}`);
    const at = entry.entry;
    // A withheld value is not shown as an empty one: the note names its type alone.
    const withheld = entry.value === null;
    const value = entry.value ?? '';
    if (at === null) {
        if (entry.blocked)
            return withheld
                ? t('providerConfig.unreadableBlockedHidden', { type })
                : t('providerConfig.unreadableBlocked', { type, value });
        return withheld
            ? t('providerConfig.unreadableHidden', { type })
            : t('providerConfig.unreadable', { type, value });
    }
    const place = at.partKey === null ? `${at.number}` : `${at.number}, ${t(at.partKey)}`;
    return withheld
        ? t('providerConfig.unreadableEntryHidden', { type, entry: place })
        : t('providerConfig.unreadableEntry', { type, value, entry: place });
}

export interface FieldWarningsProps {
    /** The id of the field's box; each stored-value note is named from it (unreadableIds). */
    id: string;
    field: ProviderFieldSchema;
    unreadable: readonly UnreadableStored[];
    conflict: ProviderFieldConflict | null;
}

/**
 * What warns under a setting: stored values it cannot show, and another user's value after a
 * conflict. Neither is announced as it appears, as before: the conflict toast says what
 * happened, and the stored-value notes describe the box itself.
 */
function FieldWarnings({ id, field, unreadable, conflict }: FieldWarningsProps) {
    const { t } = useTranslation();
    const clientAdmin = useContext(ClientAdminViewContext);
    const ids = unreadableIds(id, unreadable);
    return (
        <>
            {unreadable.map((entry, index) => (
                <Alert
                    // An entry's key is unique within one field.
                    key={entry.entry === null ? '' : `${entry.entry.number}-${entry.entry.partKey}`}
                    tone="warning"
                    size="small"
                    live="off">
                    {/* Alert takes no id, so the text carries the one the box points at. */}
                    <span id={ids[index]}>{unreadableText(t, entry, clientAdmin)}</span>
                </Alert>
            ))}
            {conflict && (
                <Alert tone="warning" size="small" live="off">
                    {t('providerConfig.conflict.theirs', {
                        // A SCADA list is JSON; its size says more than its text.
                        value:
                            field.kind === 'scadaItems'
                                ? t('providerConfig.scada.count', {
                                      count: scadaItemCount(conflict.theirs),
                                  })
                                : conflictText(conflict.theirs),
                    })}
                </Alert>
            )}
        </>
    );
}

export default FieldWarnings;
export { FieldWarnings };
